import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, NgZone, PLATFORM_ID, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';

import { EnvironmentService } from '@offconon/shared/utils/services/environment';
@Injectable({
  providedIn: 'root',
})
export class RecaptchaService {
  private readonly ngZone = inject(NgZone);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly environmentService = inject(EnvironmentService);
  private siteKey: string;

  captchaVerified = signal(false);
  recaptchaToken = signal<string | null>(null);
  captchaError = signal<string | null>(null);
  constructor() {
    this.siteKey = this.environmentService.environment.recaptchaSiteKey;
  }

  loadRecaptchaScript(): void {
    if (isPlatformBrowser(this.platformId) && !(window as any).grecaptcha) {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${this.siteKey}`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }

  executeRecaptcha(action: string): Promise<string | null> {
    this.captchaError.set(null);
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve(null);
    }

    const grecaptcha = (window as any).grecaptcha;

    if (grecaptcha && grecaptcha.ready) {
      return new Promise((resolve, reject) => {
        grecaptcha.ready(() => {
          grecaptcha
            .execute(this.siteKey, { action })
            .then((token: string) => {
              this.ngZone.run(() => {
                this.onCaptchaResolved(token);
                resolve(token);
              });
            })
            .catch((error: any) => {
              this.ngZone.run(() => {
                const errorMessage = this.translate.instant(
                  'Failed to verify reCAPTCHA. Please try again.',
                );
                this.captchaError.set(errorMessage);
                this.messageService.add({
                  severity: 'error',
                  summary: this.translate.instant('Error'),
                  detail: errorMessage,
                });
                reject(error);
              });
            });
        });
      });
    } else {
      const errorMessage = this.translate.instant(
        'reCAPTCHA service is not available. Please refresh the page and try again.',
      );
      this.captchaError.set(errorMessage);
      return Promise.reject(new Error(errorMessage));
    }
  }

  onCaptchaResolved(token: string): void {
    this.recaptchaToken.set(token);
    this.captchaVerified.set(true);
    this.captchaError.set(null);
  }

  onCaptchaExpired(): void {
    this.recaptchaToken.set(null);
    this.captchaVerified.set(false);
    this.captchaError.set(
      this.translate.instant('reCAPTCHA verification expired. Please try again.'),
    );
  }

  getToken(): string | null {
    return this.recaptchaToken();
  }

  getError(): string | null {
    return this.captchaError();
  }

  getSiteKey(): string {
    return this.siteKey;
  }

  isTokenValid(): boolean {
    return !!this.recaptchaToken() && this.captchaVerified();
  }

  resetCaptchaState(): void {
    this.captchaVerified.set(false);
    this.recaptchaToken.set(null);
    this.captchaError.set(null);
  }
  hideRecaptchaWidget(): void {
    if (isPlatformBrowser(this.platformId)) {
      const recaptchaElements = document.querySelectorAll('.grecaptcha-badge');
      recaptchaElements.forEach((element) => {
        (element as HTMLElement).style.visibility = 'hidden';
      });
    }
  }
}
